import React from "react";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import logo from "../assets/images/SideBarLogo.png";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import QrCodeOutlinedIcon from "@mui/icons-material/QrCodeOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { colors } from "../theme/colors";

const sections = [
  {
    key: "Dashboard",
    label: "Dashboard",
    path: "/dashboard/overview",
    icon: <DashboardOutlinedIcon />,
  },
  {
    key: "Station Onboarding",
    label: "Station Onboarding",
    path: "/dashboard/station-onboarding",
    icon: <LocalGasStationOutlinedIcon />,
  },
  {
    key: "Users & Roles",
    label: "Users & Roles",
    path: "/dashboard/users-and-roles",
    icon: <PeopleOutlinedIcon />,
  },
  {
    key: "Generate QR Codes",
    label: "Generate QR Codes",
    path: "/dashboard/generate-qr-codes",
    icon: <QrCodeOutlinedIcon />,
  },
  {
    key: "Registered Vehicles",
    label: "Registered Vehicles",
    path: "/dashboard/registered-vehicles",
    icon: <DirectionsCarFilledOutlinedIcon />,
  },
  {
    key: "Audit Logs",
    label: "Audit Logs",
    path: "/dashboard/audit-logs",
    icon: <HistoryOutlinedIcon />,
  },
];

const drawerWidth = 292;

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setIsMobileDrawerOpen((previous) => !previous);
  };

  const isSectionActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  React.useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (isDesktop) {
      setIsMobileDrawerOpen(false);
    }
  }, [isDesktop]);

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        px: 2.5,
        py: 3,
        background: `linear-gradient(180deg, ${colors.darkGreen} 0%, #154719 100%)`,
        color: colors.white,
      }}
    >
      <Box
        component={NavLink}
        to="/dashboard/overview"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
          py: 1.5,
          borderRadius: 3,
          backgroundColor: alpha(colors.white, 0.05),
          border: `1px solid ${alpha(colors.white, 0.08)}`,
          textDecoration: "none",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="C-Raksha"
          sx={{
            width: "100%",
            maxWidth: 190,
            height: 50,
            objectFit: "fill",
          }}
        />
      </Box>

      <List disablePadding sx={{ display: "grid", gap: 1 }}>
        {sections.map((section) => {
          const isActive = isSectionActive(section.path);

          return (
            <ListItem key={section.key} disablePadding>
              <ListItemButton
                component={NavLink}
                to={section.path}
                selected={isActive}
                sx={{
                  minHeight: 52,
                  borderRadius: 3,
                  px: 1.75,
                  color: isActive ? colors.white : alpha(colors.white, 0.78),
                  backgroundColor: isActive ? colors.primary : "transparent",
                  boxShadow: isActive
                    ? "0 14px 32px rgba(10, 61, 19, 0.28)"
                    : "none",
                  transition:
                    "background-color 0.2s ease, color 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    backgroundColor: isActive
                      ? colors.primary
                      : alpha(colors.white, 0.08),
                    transform: "translateX(4px)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: colors.primary,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: colors.primary,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: "inherit",
                  }}
                >
                  {section.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      component="span"
                      sx={{
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 600,
                        letterSpacing: 0.2,
                      }}
                    >
                      {section.label}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background:
          "linear-gradient(180deg, #EEF5EE 0%, #F7FBF7 50%, #FDFEFD 100%)",
        color: "#16301A",
      }}
    >
      {!isDesktop && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: alpha(colors.darkGreen, 0.96),
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${alpha(colors.white, 0.08)}`,
          }}
        >
          <Toolbar sx={{ minHeight: 72, px: 2 }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open navigation"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5 }}
            >
              <MenuRoundedIcon />
            </IconButton>

            <Box
              component={NavLink}
              to="/dashboard/overview"
              sx={{ display: "flex", textDecoration: "none" }}
            >
              <Box
                component="img"
                src={logo}
                alt="C-Raksha"
                sx={{
                  width: 140,
                  height: 36,
                  objectFit: "fill",
                }}
              />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop ? true : isMobileDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: `1px solid ${alpha(colors.darkGreen, 0.08)}`,
              boxShadow: isDesktop
                ? "10px 0 28px rgba(27, 94, 32, 0.08)"
                : "0 18px 44px rgba(9, 40, 14, 0.28)",
              overflow: "hidden",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          pt: { xs: 9, md: 0 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Dashboard;
